<template>
  <div id="ur-autofill-dropdown-content" class="p-3" style="width: 300px">
    <b-form-group
      label-size="sm"
      label="Amount of units to assign"
      :state="formErrors.amount.length == 0 ? null : false"
      :invalid-feedback="formErrors.amount.join(', ')"
    >
      <template v-slot:label>
        Amount of units to assign <span class="text-danger">*</span>
      </template>
      <b-form-input
        size="sm"
        type="number"
        v-model="form.amount"
        trim
      ></b-form-input>
    </b-form-group>
    <b-form-group
      label-size="sm"
      label="Starting on"
      :state="formErrors.startDate.length == 0 ? null : false"
      :invalid-feedback="formErrors.startDate.join(', ')"
    >
      <template v-slot:label>
        Starting on <span class="text-danger">*</span>
      </template>
      <div class="text-center">
        <b-calendar
          hide-header
          v-model="form.startDate"
          :date-format-options="{
            year: 'numeric',
            day: '2-digit',
            month: '2-digit'
          }"
          width="260px"
          :min="restrictDates ? this.startDate : null"
          :max="restrictDates ? this.endDate : null"
        ></b-calendar>
      </div>
    </b-form-group>
    <b-form-group class="my-4" label="">
      <div class="d-flex justify-content-start align-items-center">
        <span class="text-nowrap mr-2" style="font-size:14px;"
          >Repeat every</span
        >
        <b-form-input
          v-model="form.frequencyNumber"
          class="frequency-number mr-2"
          type="number"
          number
          min="1"
          placeholder="1"
          size="sm"
        ></b-form-input>
        <b-form-select
          v-model="form.frequencyUnit"
          :options="recurrencePeriods"
          class="frequency-unit"
          size="sm"
        ></b-form-select>
      </div>
    </b-form-group>
    <b-form-group
      v-if="form.frequencyUnit == 'weeks'"
      class="mt-2"
      label="Repeat on"
    >
      <week-days-picker size="sm" align="left" v-model="form.weekDays" />
    </b-form-group>
    <b-form-group
      v-if="form.frequencyUnit == 'months'"
      class="mt-2"
      label="Repeat on"
    >
      <b-form-select
        size="sm"
        v-model="form.monthRecurrence"
        :options="monthRecurrenceOptions"
        class="month-recurrence-input"
      ></b-form-select>
    </b-form-group>
    <div class="text-center">
      <b-button size="sm" variant="primary" class="px-4" @click="autoFillDates">
        Assign
      </b-button>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import WeekDaysPicker from "../controls/WeekDaysPicker";
import {
  validateNumberLessEqualThan,
  validateRequired
} from "../../helpers/validation";
import { formatDateOnlyMysql } from "../../helpers/date";
import { mapGetters } from "vuex";

export default {
  name: "UtilizationAutofillDropdownContent",
  components: { WeekDaysPicker },
  props: ["numberOfUnits", "startDate", "endDate", "restrictDates"],
  data() {
    return {
      form: {
        amount: 0,
        startDate: null,
        frequencyNumber: 1,
        frequencyUnit: "days",
        weekDays: [],
        monthRecurrence: ""
      },
      formErrors: {
        amount: [],
        startDate: []
      },
      recurrencePeriods: [
        {
          value: "days",
          text: "days"
        },
        {
          value: "weeks",
          text: "weeks"
        },
        {
          value: "months",
          text: "months"
        },
        {
          value: "years",
          text: "years"
        }
      ]
    };
  },
  mounted() {
    if (this.numberOfUnits) this.form.amount = this.numberOfUnits;
    if (this.startDate) {
      this.form.startDate = this.startDate;
    } else if (
      !this.restrictDates ||
      !this.endDate ||
      formatDateOnlyMysql(this.getCurrentAdjustedDateTime()) <= this.endDate
    ) {
      this.form.startDate = formatDateOnlyMysql(
        this.getCurrentAdjustedDateTime()
      );
    }
  },
  methods: {
    validateAutoFillDates() {
      let withoutErrors = true;

      this.formErrors.amount = [];
      if (!validateRequired(this.form.amount)) {
        this.formErrors.amount.push("This field is required");
        withoutErrors = false;
      } else if (
        !validateNumberLessEqualThan(this.form.amount, this.numberOfUnits)
      ) {
        this.formErrors.amount.push("Maximum value of " + this.numberOfUnits);
        withoutErrors = false;
      }

      this.formErrors.startDate = [];
      if (!validateRequired(this.form.startDate)) {
        this.formErrors.startDate.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async autoFillDates() {
      const result = await this.validateAutoFillDates();
      if (!result) {
        return;
      }
      let assignedDates = [];
      let currentDate = moment(this.form.startDate).toDate();
      if (this.isValidStartDate(currentDate)) {
        assignedDates.push(formatDateOnlyMysql(currentDate));
      }
      while (assignedDates.length < this.form.amount) {
        currentDate = this.getNextDate(currentDate);
        if (this.endDate && formatDateOnlyMysql(currentDate) > this.endDate) {
          break;
        }
        assignedDates.push(formatDateOnlyMysql(currentDate));
      }
      this.$emit("autoFill", assignedDates);
    },
    isValidStartDate(date) {
      switch (this.form.frequencyUnit) {
        case "days":
          return true;
        case "years":
          return true;
        case "weeks":
          if (
            this.form.weekDays.indexOf(
              moment(date)
                .day()
                .toString()
            ) >= 0
          ) {
            return true;
          }
          return false;
        case "months":
          return true;
        default:
          return true;
      }
    },
    getNextDate(currentDate) {
      let iterationDate = null;
      switch (this.form.frequencyUnit) {
        case "days":
          return moment(currentDate)
            .add(this.form.frequencyNumber, "days")
            .toDate();
        case "years":
          return moment(currentDate)
            .add(this.form.frequencyNumber, "years")
            .toDate();
        case "weeks":
          iterationDate = moment(currentDate).add(1, "day");
          if (iterationDate.day() == 0)
            iterationDate = iterationDate.add(
              this.form.frequencyNumber - 1,
              "weeks"
            );
          while (iterationDate) {
            if (this.form.weekDays.indexOf(iterationDate.day().toString()) >= 0)
              return iterationDate.toDate();
            iterationDate = iterationDate.add(1, "day");
            if (iterationDate.day() == 0)
              iterationDate = iterationDate.add(
                this.form.frequencyNumber - 1,
                "weeks"
              );
          }
          return null;
        case "months":
          if (this.form.monthRecurrence == "ByDayOfMonth") {
            let frecuency = this.form.frequencyNumber;
            // eslint-disable-next-line no-constant-condition
            while (true) {
              let temporalDate = moment(currentDate)
                .add(frecuency, "months")
                .set("date", 1);
              let endOfMonth = moment(temporalDate).endOf("month");

              if (endOfMonth.date() < moment(currentDate).date()) {
                frecuency += this.form.frequencyNumber;
              } else {
                break;
              }
            }

            return moment(currentDate)
              .add(frecuency, "months")
              .toDate();
          } else {
            let frecuency = this.form.frequencyNumber;
            const weekDayName = moment(currentDate).day();
            const weekOfTheMonth = Math.ceil(moment(currentDate).date() / 7);

            if (weekOfTheMonth == 5) {
              // eslint-disable-next-line no-constant-condition
              while (true) {
                let temporalDate = moment(currentDate)
                  .add(frecuency, "months")
                  .set("date", 1);
                let endOfMonth = moment(temporalDate).endOf("month");
                const lastNewOcurrency = moment(temporalDate).set(
                  "date",
                  endOfMonth.date() -
                    (weekDayName < endOfMonth.day()
                      ? endOfMonth.day() - weekDayName
                      : weekDayName - endOfMonth.day())
                );
                if (Math.ceil(lastNewOcurrency.date() / 7) == 5) {
                  break;
                } else {
                  frecuency += this.form.frequencyNumber;
                }
              }
            }

            iterationDate = moment(currentDate)
              .add(frecuency, "months")
              .set("date", 1);

            if (iterationDate.day() > weekDayName) {
              iterationDate = moment(iterationDate).add(
                7 - (iterationDate.day() - weekDayName),
                "days"
              );
            } else if (iterationDate.day() < weekDayName) {
              iterationDate = moment(iterationDate).add(
                weekDayName - iterationDate.day(),
                "days"
              );
            }

            while (iterationDate) {
              if (
                weekOfTheMonth == Math.ceil(moment(iterationDate).date() / 7)
              ) {
                return iterationDate.toDate();
              }
              iterationDate.add(7, "days");
            }

            return null;
          }
        default:
          return null;
      }
    }
  },
  computed: {
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    monthRecurrenceOptions() {
      const date = moment(this.form.startDate, "yyyy-MM-DD");
      const day = date.date();
      const weekDayName = date.format("dddd");
      const prefixes = ["first", "second", "third", "fourth", "fifth"];
      let weekLabel = null;
      if (day % 7 == 0) {
        weekLabel = prefixes[Math.floor((day - 1) / 7)];
      } else {
        weekLabel = prefixes[Math.floor(day / 7)];
      }

      return [
        {
          value: "ByDayOfMonth",
          text: `Monthly, on day ${day}`
        },
        {
          value: "ByDayOfWeek",
          text: `Monthly, on the ${weekLabel} ${weekDayName}`
        }
      ];
    }
  }
};
</script>

<style>
#ur-autofill-dropdown-content .calendar-content {
  padding-right: 13px;
}
#ur-autofill-dropdown-content .calendar-content .b-calendar .b-monthview {
  padding-top: 0px !important;
}
#ur-autofill-dropdown-content
  .calendar-content
  .b-monthview
  .b-calendar-row.b-calendar-weekdays
  .b-calendar-day-header {
  font-size: 12px;
}
#ur-autofill-dropdown-content
  .calendar-content
  .b-monthview
  .b-monthview-content {
  background-color: #f5f8fc;
}
#ur-autofill-dropdown-content .calendar-content .b-cal-event-wrap.b-allday {
  opacity: 1 !important;
}
#ur-autofill-dropdown-content .calendar-content .b-top-toolbar {
  display: none;
}
#ur-autofill-dropdown-content
  .calendar-content
  .b-monthview
  .b-weeks-container
  .b-calendar-row
  .b-calendar-cell {
  overflow: initial !important;
}
#ur-autofill-dropdown-content .calendar-content .b-monthview .b-week-num {
  display: none;
}
#ur-autofill-dropdown-content
  .calendar-content
  .b-monthview
  .b-cal-cell-header
  .b-day-name {
  overflow: initial !important;
}
#ur-autofill-dropdown-content
  .calendar-content
  .b-cal-event-bar-container
  .b-cal-event {
  padding: 0px !important;
}
#ur-autofill-dropdown-content .calendar-content .b-cal-event-body {
  padding: 0px !important;
  width: 100%;
}
#ur-autofill-dropdown-content .calendar-content .event-name-label {
  font-size: 12px;
}
#ur-autofill-dropdown-content .calendar-content .b-day-name {
  width: 100%;
}
#ur-autofill-dropdown-content
  .b-calendar-week
  .b-calendar-days
  .b-calendar-cell:first-child
  .loc-list {
  right: initial !important;
  left: 0px !important;
}
#ur-autofill-dropdown-content .b-grid-header-resizable {
  background-color: #0062cc;
  color: white;
  align-items: center;
}
#ur-autofill-dropdown-content .b-grid-header-resizable:hover {
  background-color: #0062cc !important;
}
.vs__deselect {
  margin-left: 5px !important;
}
#ur-autofill-dropdown-content .b-calendar {
  display: initial !important;
}
#ur-autofill-dropdown-content .b-sidebar {
  display: initial !important;
  position: initial !important;
  width: initial !important;
}
#ur-autofill-dropdown-content .b-calendar .b-calendar-content {
  height: calc(100vh - 220px) !important;
}
#ur-autofill-dropdown-content .b-float-root {
  z-index: 10 !important;
}
#ur-autofill-dropdown-content .b-calendar .b-calendar-inner .font-weight-bold {
  font-weight: 400 !important;
  font-size: 15px;
}
#ur-autofill-dropdown-content .b-calendar .b-calendar-inner .text-muted {
  color: #d8d8d8 !important;
}

#ur-autofill-dropdown-content .b-calendar-nav {
  position: relative;
}
#ur-autofill-dropdown-content .b-calendar-nav button {
  display: none !important;
}
#ur-autofill-dropdown-content .b-calendar-nav button:nth-child(2) {
  display: block !important;
  flex: none !important;
  position: absolute;
  right: 30px;
  top: 5px;
}
#ur-autofill-dropdown-content .b-calendar-nav button:nth-child(4) {
  display: block !important;
  flex: none !important;
  position: absolute;
  right: 0px;
  top: 5px;
}
#ur-autofill-dropdown-content .b-calendar-grid-weekdays.border-bottom {
  border-bottom: none !important;
}

#ur-autofill-dropdown-content .b-calendar .b-calendar-inner .font-weight-bold {
  font-weight: 400 !important;
}
#ur-autofill-dropdown-content .b-calendar .b-calendar-inner .text-muted {
  color: #d8d8d8 !important;
}
#ur-autofill-dropdown-content .b-calendar-grid-help {
  display: none;
}
#ur-autofill-dropdown-content .b-calendar .b-calendar-grid {
  border: none;
}
#ur-autofill-dropdown-content .b-calendar .b-calendar-inner {
  min-width: 200px !important;
}
#ur-autofill-dropdown-content .b-calendar-grid-caption.text-center {
  text-align: left !important;
}
#ur-autofill-dropdown-content .b-calendar .b-calendar-grid {
  background-color: transparent !important;
}
#ur-autofill-dropdown-content .b-calendar > .b-top-toolbar {
  background-color: white !important;
}
#ur-autofill-dropdown-content label {
  margin-bottom: 0px !important;
}
#ur-autofill-dropdown-content .font-size-icon::before {
  font-size: 17px !important;
}
#ur-autofill-dropdown-content .little-badge {
  font-size: 11px;
}
#ur-autofill-dropdown-content {
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}
#ur-autofill-dropdown-content
  .b-calendar
  .b-calendar-grid-body
  .col[data-date]
  .btn,
#ur-autofill-dropdown-content .b-calendar-grid-weekdays.border-bottom small {
  font-size: 12px !important;
}
#ur-autofill-dropdown-content .form-group legend {
  font-size: 15px !important;
  color: #495057;
}
#ur-autofill-dropdown-content .calendar-content .loc-option-trigger {
  font-size: 12px;
  color: #646a7a;
  margin-right: 1px;
}
#ur-autofill-dropdown-content .calendar-content .loc-option-trigger:hover {
  background-color: #d8d8d8;
}
#ur-autofill-dropdown-content .calendar-content .loc-value:hover {
  background-color: #f5f8fc;
}
#ur-autofill-dropdown-content
  .calendar-content
  .b-cal-event-bar-container
  .b-cal-event {
  background-color: transparent !important;
}
#ur-autofill-dropdown-content
  .calendar-content
  .b-cal-event-body
  .b-cal-event-desc {
  padding: 0px 2px;
}
</style>
